import { useState, useEffect } from 'react';
import { InMemoryCache } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { Toaster } from 'sonner';
import { createApolloClient, restoreCache } from '@scio3/graphql-client';
import { AuthProvider } from '@/contexts/AuthProvider';
import { BrandingProvider } from '@/features/branding/BrandingProvider';
import { AppRouter } from '@/routes';
import { getTokenString } from '@/lib/firebase';
import { DEV_FAKE_DELAY_MS } from '@/components/shared/FolderItemList';
import { useCachePersistence } from '@/hooks/useCachePersistence';
import { ConfirmDialog } from '@/components/shared';
import { prefetchRouteChunks } from '@/lib/prefetch-routes';

export function App() {
  const [apolloClient, setApolloClient] = useState<ApolloClient | null>(null);

  // Prefetch Templates + Designer chunks on app mount (auth-independent).
  // JS chunk downloads don't need authentication — start early for faster navigation.
  useEffect(() => {
    prefetchRouteChunks();
  }, []);

  useEffect(() => {
    async function init() {
      const cache = new InMemoryCache({
        typePolicies: {
          // ScheduleRepeatObject uses `id` as a repeat-type key ("daily", "weekly", etc.)
          // NOT a unique entity ID. Without keyFields:false, all items sharing the same
          // repeat type would reference a single shared cache entry, causing cross-query
          // overwrites (e.g., refetchQueries strips DTSTART from the rrule string).
          ScheduleRepeatObject: { keyFields: false },
          // PlaylistItem _id is the referenced asset ID, NOT unique per item.
          // Duplicate assets in a playlist share the same _id but different uuid.
          // Without this, Apollo merges duplicates into one cache entry, causing
          // duration edits on one item to overwrite the other.
          PlaylistItem: { keyFields: false },
          // Company sub-objects lack _id fields. Without merge policies Apollo
          // cannot reconcile incoming data with cached data from other queries
          // (e.g. GetCompanyInfo vs GetCompanyPreferences), causing stale reads.
          Company: {
            fields: {
              feature: { merge: true },
              featureData: { merge: true },
              limitation: { merge: true },
            },
          },
        },
      });
      await restoreCache(cache);
      const client = createApolloClient({
        getToken: getTokenString,
        graphqlUrl: import.meta.env.VITE_GRAPHQL_URL,
        cache,
        // DEV-only: set VITE_DEV_FAKE_DELAY=1 to add uniform 1.5s link latency.
        devFakeDelayMs:
          import.meta.env.DEV && import.meta.env.VITE_DEV_FAKE_DELAY === '1'
            ? DEV_FAKE_DELAY_MS
            : 0,
      });
      setApolloClient(client);
    }
    init();
  }, []);

  if (!apolloClient) return null;

  return (
    <ApolloProvider client={apolloClient}>
      <CachePersistenceManager />
      <BrandingProvider>
        <AuthProvider>
          <AppRouter />
          {/* z-index must sit above every app overlay so confirmation toasts are never
            hidden behind the header/banner or a full-screen overlay (#1369). The app's
            highest overlays (Scio1Frame, WelcomeFlow) use z-[9999]; tying at 9999 let the
            toast lose by DOM order. Use sonner's max default so toasts always render on top. */}
          <Toaster
            position="top-right"
            richColors
            closeButton
            style={{ zIndex: 999999999 }}
            toastOptions={{
              classNames: {
                // Render toast actions (e.g. Undo) as an inline text link sitting right next to the
                // message (ms-2 overrides sonner's push-to-right), dark + underlined-on-hover so
                // it's clearly clickable and distinct from the toast's colored text. Not a button.
                actionButton:
                  '!ms-2 !bg-transparent !text-foreground !px-0 !h-auto !shadow-none !font-medium underline-offset-2 hover:underline cursor-pointer',
                // Circular close button in the top-RIGHT corner (mirror sonner's default top-left
                // offset). Neutral white circle with a prominent gray outline + a dark, bold X
                // (not the toast variant's color tint). [&_svg]:!stroke-[2.5] thickens the X.
                closeButton:
                  '!left-auto !right-0 ![transform:translate(35%,-35%)] !bg-background !border-muted-foreground/60 !text-foreground hover:!bg-accent [&_svg]:!size-3.5 [&_svg]:!stroke-[2.5]',
              },
            }}
          />
          <ConfirmDialog />
        </AuthProvider>
      </BrandingProvider>
    </ApolloProvider>
  );
}

/** Activates auto-persist to IndexedDB. Must be inside ApolloProvider. */
function CachePersistenceManager() {
  useCachePersistence();
  return null;
}
